import type { MultiSelectProps, DropdownSelectProps, DropdownSelectOption } from '@paintscout/ui'
import { getFeature, getObjectLabels } from '@paintscout/util/builder'
import { DropdownSelect, useClientOptions } from '@paintscout/ui'
import React from 'react'
import { useGetCorporateTypeDataQuery } from '@paintscout/api'

export interface CorporateQuoteTypeSelectProps extends Omit<DropdownSelectProps, 'value' | 'options' | 'onChange'> {
  value: string[]
  hideAll?: boolean
  /**
   * Label to use for "all" quote type. Set to falsey to fallback on label in options
   */
  allLabel?: string
  onChange?: (value: string[]) => any
}

export default function CorporateQuoteTypeSelect({
  value,
  allLabel,
  onChange,
  ...props
}: CorporateQuoteTypeSelectProps) {
  const { options } = useClientOptions()

  const {
    loading: corporateTypeDataLoading,
    data: corporateTypeData,
    error: _corporateTypeDataError
  } = useGetCorporateTypeDataQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false
  })
  const corporateTypeOptions = corporateTypeData?.getCorporateTypeData?.quoteTypes ?? []
  // We get back quote type options from all our companies, but we need to make sure we match up the ones that are the
  // same but with different ids so we can search by all relevant ids if a dupe name is picked;/
  // So will need to wrap our onchange to give back the right [val]
  // dedupe based on label, and create a mapping of what keys that are different but have the same label
  const dedupedOptions = corporateTypeOptions.reduce<{
    options: { key: string; label: string }[]
    labelToKeysMap: Record<string, string[]>
  }>(
    (acc, option) => {
      const existing = acc.options.find((opt) => opt.label === option.label)
      if (existing) {
        acc.labelToKeysMap[option.label].push(option.key)
      } else {
        acc.options.push(option)
        acc.labelToKeysMap[option.label] = [option.key]
      }
      return acc
    },
    { options: [], labelToKeysMap: {} }
  )

  const corporateTypeOptionsDeduped = dedupedOptions.options
  const labelToKeysMap = dedupedOptions.labelToKeysMap

  const { quote: quoteLabel } = getObjectLabels({ options })
  const hideAll = getFeature({ options, path: 'quotes.hideAllQuoteType' }) || props.hideAll
  const typeOptions = corporateTypeOptionsDeduped
    .filter((type) => !(hideAll && type.key === 'all'))
    .map((type) => ({
      value: type.key,
      label: type.key === 'all' && allLabel ? allLabel : type.label,
      forceTop: type.key === 'all'
    }))
    .sort((a) => (a.value === 'all' ? -1 : 1))

  const corporateSelectProps = {
    ...props,
    value: typeOptions.filter((opt) => value.includes(opt.value)),
    onChange: (options: DropdownSelectOption[]) => {
      // Modify the onchange wrapper to include all shared keys from the labelToKeysMap
      onChange?.(Array.from(new Set(options.flatMap((option) => labelToKeysMap[option.label] || [option.value]))))
    },
    variant: 'multi',
    options: typeOptions
  } as MultiSelectProps

  return (
    <DropdownSelect
      placeholder={{ selectPlaceholder: `All ${quoteLabel.value.toLowerCase()} types` }}
      loading={corporateTypeDataLoading}
      hideCloseButton
      {...corporateSelectProps}
    />
  )
}
